/**
 * Theme utilities for managing theme state
 */

export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Apply a theme to the document
 */
export function applyTheme(theme: ThemeMode): void {
  if (typeof window === 'undefined') return;

  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.removeAttribute('data-theme');

  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    root.classList.add(systemTheme);
    root.setAttribute('data-theme', systemTheme);
    return;
  }

  root.classList.add(theme);
  root.setAttribute('data-theme', theme);
}

/**
 * Toggle between light and dark themes
 */
export function toggleTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';

  const root = window.document.documentElement;
  const currentTheme = root.classList.contains('dark') ? 'dark' : 'light';
  const newTheme: ThemeMode = currentTheme === 'dark' ? 'light' : 'dark';

  applyTheme(newTheme);
  return newTheme;
}

/**
 * Initialize theme from localStorage or system preference
 */
export function initializeTheme(storageKey = 'sf-ui-theme'): ThemeMode {
  if (typeof window === 'undefined') return 'light';

  const stored = localStorage.getItem(storageKey) as ThemeMode | null;
  const theme = stored || 'system';

  applyTheme(theme);
  return theme;
}

/**
 * Get the current resolved theme (not 'system')
 */
export function getResolvedTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';

  const root = window.document.documentElement;
  return root.classList.contains('dark') ? 'dark' : 'light';
}
